import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Observable, of, throwError, timer } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { AuthTokenService } from 'src/app/auth/auth-token.service';
import { Login } from 'src/app/auth/login/login.model';
import { AlertService } from 'src/app/shared/services/alert.service';

import { MockDb } from '../mock-db';
import { findMockUser } from '../fixtures/users.fixture';

const MOCK_LATENCY_MS = 300;

/**
 * Stands in for AuthTokenService when `environment.useMock` is true.
 * Specified in contracts-1 §11.4.
 *
 * Extends rather than replaces the real service so the DI signature and the public type stay
 * intact — AuthService, LoginComponent and AccountGuard are untouched by the swap.
 *
 * Deliberately never sets `userStatus`, so the force-password-change branch in LoginComponent
 * cannot fire during a demo.
 */
@Injectable()
export class MockAuthTokenService extends AuthTokenService {
  constructor(
    http: HttpClient,
    alertService: AlertService,
    cookieService: CookieService,
    private readonly localStorage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService,
    applicationConfigService: ApplicationConfigService,
  ) {
    super(http, alertService, cookieService, localStorage, sessionStorage, applicationConfigService);
  }

  override getToken(): string {
    return this.localStorage.retrieve('authtoken') ?? this.sessionStorage.retrieve('authtoken') ?? '';
  }

  override login(credentials: Login): Observable<any> {
    MockDb.ensureSeeded();

    return timer(MOCK_LATENCY_MS).pipe(
      switchMap(() => {
        const user = findMockUser(credentials?.username, credentials?.password);

        if (!user) {
          // LoginComponent's error handler sets authenticationError = true.
          return throwError(() => new Error('Invalid username or password'));
        }

        localStorage.removeItem('userStatus');
        this.localStorage.store('scope', user.scope);
        this.localStorage.store('authtoken', buildFakeToken(user.username));
        this.sessionStorage.store('authtoken', buildFakeToken(user.username));
        MockDb.setCurrentUser(user);

        return of({ accessToken: buildFakeToken(user.username), scope: user.scope });
      }),
    );
  }

  override logout(): Observable<void> {
    return new Observable<void>((observer) => {
      this.localStorage.clear('authtoken');
      this.sessionStorage.clear('authtoken');
      this.localStorage.clear('scope');
      MockDb.clearCurrentUser();
      observer.complete();
    }).pipe(delay(0));
  }
}

/**
 * Shaped like a JWT so anything that inspects the token superficially (length, dot count) behaves.
 * It is not signed and carries no claims of consequence — it exists only to satisfy the
 * `localStorage['authtoken']` checks scattered through the app.
 */
function buildFakeToken(username: string): string {
  const header = base64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64Url(JSON.stringify({ sub: username, iss: 'ncc-mock', iat: Date.now() }));
  return `${header}.${payload}.mock`;
}

function base64Url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
